import { useNavigate, useSearchParams } from "react-router-dom";
import { WORLD, todayString } from "@/lib/gameEngine";
import { stopMusic } from "@/lib/sfx";
import { GAME } from "@/constants/testIds";
import { useGameLoop } from "@/hooks/useGameLoop";
import GameOverModal from "@/components/GameOverModal";
import {
  Pause,
  Play as PlayIcon,
  Home,
  Volume2,
  VolumeX,
  Zap,
  Shield,
  Coins as CoinsIcon,
} from "lucide-react";

export default function Game() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const mode = searchParams.get("mode") === "daily" ? "daily" : "endless";

  const {
    canvasRef,
    stateRef,
    paused,
    setPaused,
    showOver,
    muted,
    toggleMute,
    restart,
    setTouchKey,
  } = useGameLoop({ mode });

  const st = stateRef.current;
  const score = st ? st.score : 0;
  const lives = st ? st.lives : 3;
  const block = st ? st.blockNumber : 1;
  const coins = st ? st.coinsCollected : 0;
  const tick = st ? st.tick : 0;
  const player = st ? st.player : null;

  const hasDouble = !!(player && player.hasDoubleJump);
  const shadesLeft = player
    ? Math.max(0, Math.ceil((player.shadesUntil - tick) / 60))
    : 0;
  const multLeft = player
    ? Math.max(0, Math.ceil((player.multiplierUntil - tick) / 60))
    : 0;

  return (
    <div
      className="relative min-h-screen w-full bg-black flex flex-col items-center justify-start py-6 px-3"
      data-testid="game-screen"
    >
      <div className="w-full max-w-5xl">
        {/* HUD */}
        <div className="flex items-center justify-between mb-3 px-2 gap-3 flex-wrap">
          <button
            type="button"
            onClick={() => navigate("/")}
            data-testid={GAME.backToTitleBtn}
            className="font-hud text-white/80 hover:text-white text-base inline-flex items-center gap-1.5 px-3 py-1.5 border border-white/20 hover:border-white/60 rounded transition-colors"
          >
            <Home className="w-4 h-4" /> HOME
          </button>

          <HudStats
            score={score}
            lives={lives}
            block={block}
            coins={coins}
            mode={mode}
          />

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={toggleMute}
              data-testid="mute-toggle"
              className="font-hud text-white text-base inline-flex items-center gap-1 px-3 py-1 border border-white/30 rounded hover:border-white"
              title="Mute (M)"
            >
              {muted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
            </button>
            <button
              type="button"
              onClick={() => setPaused((p) => !p)}
              data-testid={GAME.pauseBtn}
              className="font-hud text-white text-base inline-flex items-center gap-1 px-3 py-1 border border-white/30 rounded hover:border-white"
            >
              {paused ? (
                <>
                  <PlayIcon className="w-4 h-4" /> RESUME
                </>
              ) : (
                <>
                  <Pause className="w-4 h-4" /> PAUSE
                </>
              )}
            </button>
          </div>
        </div>

        {/* Power-up status */}
        <PowerupRow
          hasDouble={hasDouble}
          shadesLeft={shadesLeft}
          multLeft={multLeft}
        />

        {/* Canvas */}
        <div
          className="relative w-full red-glow rounded-md overflow-hidden"
          style={{ aspectRatio: `${WORLD.width} / ${WORLD.height}` }}
        >
          <canvas
            ref={canvasRef}
            width={WORLD.width}
            height={WORLD.height}
            className="block w-full h-full"
            data-testid={GAME.gameCanvas}
          />
          {paused && !showOver && (
            <div className="absolute inset-0 bg-black/70 flex items-center justify-center z-20">
              <div className="text-center">
                <p className="font-display text-5xl sm:text-7xl text-white tracking-tight">
                  PAUSED
                </p>
                <p className="font-hud text-[var(--crt-green)] mt-2 text-lg">
                  PRESS P / ESC TO RESUME
                </p>
              </div>
            </div>
          )}
        </div>

        <p className="font-hud text-center text-white/50 text-base mt-4">
          [← → / A D] MOVE · [SPACE / W / ↑] JUMP · [P / ESC] PAUSE · [M] MUTE
        </p>

        {/* Mobile touch controls */}
        <div className="sm:hidden mt-4 grid grid-cols-3 gap-3 max-w-md mx-auto select-none">
          <TouchBtn
            label="←"
            onDown={() => setTouchKey("left", true)}
            onUp={() => setTouchKey("left", false)}
            testId="touch-left"
          />
          <TouchBtn
            label="JUMP"
            onDown={() => setTouchKey("jump", true)}
            onUp={() => setTouchKey("jump", false)}
            testId="touch-jump"
          />
          <TouchBtn
            label="→"
            onDown={() => setTouchKey("right", true)}
            onUp={() => setTouchKey("right", false)}
            testId="touch-right"
          />
        </div>
      </div>

      {showOver && st && (
        <GameOverModal
          score={st.score}
          coins={st.coinsCollected}
          distance={st.distance}
          enemiesDefeated={st.enemiesDefeated}
          bossesDefeated={st.bossesDefeated}
          powerupsByType={st.powerupsByType}
          mode={st.mode}
          seedDate={st.seedDate}
          onRestart={restart}
          onExit={() => {
            stopMusic();
            navigate("/");
          }}
        />
      )}
    </div>
  );
}

function HudStats({ score, lives, block, coins, mode }) {
  const LIFE_SLOTS = ["life-a", "life-b", "life-c"];
  return (
    <div className="flex items-center gap-4 font-hud flex-wrap justify-center">
      <div className="text-center">
        <div className="text-white/60 text-xs tracking-[0.3em]">SCORE</div>
        <div
          data-testid={GAME.scoreDisplay}
          className="text-[var(--crt-green)] hud-glow text-2xl"
        >
          {String(score).padStart(6, "0")}
        </div>
      </div>
      <div className="text-center">
        <div className="text-white/60 text-xs tracking-[0.3em]">LIVES</div>
        <div
          data-testid={GAME.livesDisplay}
          className="flex gap-1 mt-1 justify-center"
        >
          {LIFE_SLOTS.map((slotId, i) => (
            <span
              key={slotId}
              className="w-4 h-4"
              style={{
                background:
                  i < lives ? "var(--neon-red)" : "rgba(255,255,255,0.1)",
                boxShadow:
                  i < lives ? "0 0 6px rgba(255,0,60,0.7)" : "none",
              }}
            />
          ))}
        </div>
      </div>
      <div className="text-center">
        <div className="text-white/60 text-xs tracking-[0.3em]">BLOCK</div>
        <div data-testid={GAME.blockDisplay} className="text-white text-2xl">
          {String(block).padStart(2, "0")}
        </div>
      </div>
      <div className="text-center hidden sm:block">
        <div className="text-white/60 text-xs tracking-[0.3em]">COINS</div>
        <div className="text-[var(--street-yellow)] text-2xl">
          {String(coins).padStart(3, "0")}
        </div>
      </div>
      {mode === "daily" && (
        <div className="text-center hidden md:block">
          <div className="text-white/60 text-xs tracking-[0.3em]">DAILY</div>
          <div className="text-[var(--street-yellow)] text-lg">
            {todayString()}
          </div>
        </div>
      )}
    </div>
  );
}

function PowerupRow({ hasDouble, shadesLeft, multLeft }) {
  return (
    <div className="flex items-center justify-center gap-2 mb-2 min-h-[28px] font-hud text-base">
      {hasDouble && (
        <PowerupChip
          icon={<Zap className="w-3.5 h-3.5" />}
          label="DOUBLE JUMP"
          color="#ffcc00"
          testId="powerup-chain"
        />
      )}
      {shadesLeft > 0 && (
        <PowerupChip
          icon={<Shield className="w-3.5 h-3.5" />}
          label={`INVINCIBLE ${shadesLeft}s`}
          color="#39ff14"
          testId="powerup-shades"
        />
      )}
      {multLeft > 0 && (
        <PowerupChip
          icon={<CoinsIcon className="w-3.5 h-3.5" />}
          label={`x2 COINS ${multLeft}s`}
          color="#ff003c"
          testId="powerup-cup"
        />
      )}
    </div>
  );
}

function PowerupChip({ icon, label, color, testId }) {
  return (
    <span
      data-testid={testId}
      className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border-2"
      style={{
        borderColor: color,
        color,
        boxShadow: `0 0 10px ${color}66`,
        background: "rgba(0,0,0,0.55)",
      }}
    >
      {icon}
      <span className="text-sm tracking-wider">{label}</span>
    </span>
  );
}

function TouchBtn({ label, onDown, onUp, testId }) {
  return (
    <button
      type="button"
      data-testid={testId}
      onPointerDown={(e) => {
        e.preventDefault();
        onDown();
      }}
      onPointerUp={onUp}
      onPointerLeave={onUp}
      onPointerCancel={onUp}
      className="font-hud text-2xl text-white py-5 bg-black/60 border-2 border-[var(--neon-red)]/60 active:bg-[var(--neon-red)]/30 rounded-lg"
    >
      {label}
    </button>
  );
}
